/**
 * Admin REST endpoints under /api/v1/admin/*.
 *
 * Backend contract — see `system/api/routes/admin.py` (built separately).
 * Cookie-based auth (Slack magic-link or Microsoft OAuth); 401/403 if the
 * caller isn't admin.
 *
 * One module, hooks per endpoint (mirrors `jarvis.ts` etc). Mutations
 * invalidate the relevant query keys on success — components don't track
 * freshness manually.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import { api } from "./client";

// ---------- Domain shapes ----------

export interface AdminUser {
  id: string;
  email: string;
  name?: string | null;
  slack_user_id?: string | null;
  cron_enabled: boolean;
  default_slack_channel?: string | null;
  disabled_at?: string | null;
  created_at: string;
  role_names: string[];
}

export interface AdminRole {
  id: string;
  name: string;
  workspace_slug: string;
  description?: string | null;
  tool_names: string[];
}

export interface AdminTool {
  name: string;
  category: string;
  workspaces: string[];
  mcp_exposed: boolean;
  mcp_dangerous_default: boolean;
  audit_safe_args: string[];
  /** null → use mcp_dangerous_default; true → always confirm; false → never. */
  requires_confirmation_override?: boolean | null;
}

export interface AuditEntry {
  id: string;
  user_email: string;
  tool_name: string;
  args_json: Record<string, unknown>;
  result_code: string;
  mcp_session_id?: string | null;
  created_at: string;
}

export interface DenialEntry {
  id: string;
  attempted_email?: string | null;
  source_ip?: string | null;
  reason: string;
  created_at: string;
}

export interface SessionEntry {
  id: string;
  user_email?: string | null;
  workspace?: string | null;
  activated: boolean;
  created_at: string;
  last_seen_at: string;
}

export type ConfirmationStatus =
  | "pending"
  | "accepted"
  | "executing"
  | "executed"
  | "denied"
  | "timeout";

export interface ConfirmationEntry {
  id: string;
  tool_name: string;
  args_json: Record<string, unknown>;
  args_preview?: string | null;
  status: ConfirmationStatus;
  created_at: string;
  resolved_at?: string | null;
  resolved_by?: string | null;
  mcp_session_id?: string | null;
  user_email?: string | null;
}

export interface ScheduledJob {
  id: string;
  user_id: string;
  name: string;
  frequency: string;
  earliest_time_of_day?: string | null;
  latest_time_of_day?: string | null;
  prompt_template: string;
  enabled: boolean;
  created_at: string;
}

export interface AdminHealth {
  cron_users: number;
  active_sessions: number;
  pending_confirmations: number;
  audit_last_24h: number;
  denials_last_24h: number;
  ltm_disk_bytes: number;
}

export interface OnboardingEmailDraft {
  subject: string;
  body: string;
}

export interface AdminNotificationTarget {
  type: string;
  config: Record<string, unknown>;
}

export interface AdminCronJob {
  id: string;
  name: string;
  scope_type: string;
  scope_id: string;
  scope_label?: string | null;
  schedule?: string | null;
  schedule_description?: string | null;
  instruction?: string | null;
  model?: string | null;
  timeout: number;
  enabled: boolean;
  notification_target?: AdminNotificationTarget | null;
  dispatch_mode?: "shared" | "per_member";
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AdminCronJobCreate {
  name: string;
  scope_type: string;
  scope_id: string;
  schedule?: string | null;
  instruction?: string | null;
  model?: string | null;
  timeout?: number;
  enabled?: boolean;
  notification_target?: AdminNotificationTarget | null;
}

// No additional fields beyond the create shape's keys, all optional.
export type AdminCronJobPatch = Partial<AdminCronJobCreate>;

export interface AdminCronJobTemplate {
  name: string;
  schedule?: string | null;
  instruction: string;
  model?: string | null;
  timeout: number;
}

export interface AdminCronValidateResult {
  valid: boolean;
  description?: string | null;
  next_runs: string[];
  error?: string | null;
}

export interface AdminCronTokensValidateResult {
  valid: boolean;
  used_tokens: string[];
  valid_tokens: string[];
  unknown_tokens: string[];
  /** Literal braces the renderer would read as replacement fields. */
  malformed_fields: string[];
  error?: string | null;
}

export interface AdminCronSyncResult {
  added: string[];
  updated: string[];
  deleted: string[];
  skipped: string[];
}

export interface AdminScopeType {
  type_name: string;
  available: boolean;
  valid_tokens: string[];
}

export interface AdminScopeChoice {
  scope_id: string;
  label: string;
  sublabel?: string | null;
  disabled_reason?: string | null;
}

export type AdminScopeGroupType = "team" | "project";

export interface AdminScopeGroup {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  member_count: number;
  created_at?: string | null;
}

export interface AdminScopeGroupMember {
  user_id: string;
  email: string;
  name: string;
  role: string;
}

export interface AdminScopeGroupDetail {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  members: AdminScopeGroupMember[];
  created_at?: string | null;
}

export interface AdminScopeGroupCreate {
  name: string;
  slug: string;
  description?: string | null;
}

export interface AdminScopeGroupPatch {
  // Slug is immutable server-side — not part of the patch shape.
  name?: string;
  description?: string | null;
}

// ---------- AI team ----------

/** Employee state words from `system/services/ai_team.py::classify_agent_state`. */
export type AgentState =
  | "suspended"
  | "login_expired"
  | "stuck"
  | "offline"
  | "deaf"
  | "backlogged"
  | "working"
  | "idle";

/** The eight canonical `pending_jobs` states. */
export type JobStatus =
  | "submitted"
  | "working"
  | "input_required"
  | "auth_required"
  | "completed"
  | "failed"
  | "canceled"
  | "rejected";

export interface AgentToken {
  id: string;
  token_prefix: string;
  label?: string | null;
  /** active | revoked | expired */
  state: string;
  created_at?: string | null;
  last_used_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
}

export interface AiTeamCurrentJob {
  id: string;
  name: string;
  started_at?: string | null;
}

export interface AiTeamAgent {
  id: string;
  agent_slug?: string | null;
  name?: string | null;
  email: string;
  owner_user_id?: string | null;
  disabled_at?: string | null;
  last_seen_at?: string | null;
  state: AgentState;
  /** An event receiver holds the employee's stream right now (server-side, live). */
  stream_connected?: boolean;
  /** The runner's fresh heartbeat reports Claude Code rejecting the seat's login (null = no fresh report). */
  model_login_failed?: boolean | null;
  current_job?: AiTeamCurrentJob | null;
  submitted_count: number;
  working_count: number;
  parked_count: number;
  open_questions: number;
  oldest_submitted_age_s?: number | null;
  oldest_working_age_s?: number | null;
  last_digest_at?: string | null;
}

export interface AiTeamAgentCron {
  id: string;
  name: string;
  schedule?: string | null;
  enabled: boolean;
  model?: string | null;
  timeout: number;
}

export interface AiTeamAgentDetail extends AiTeamAgent {
  role_names: string[];
  tokens: AgentToken[];
  cron_jobs: AiTeamAgentCron[];
}

export interface AiTeamJob {
  id: string;
  job_type: string;
  args?: Record<string, unknown> | null;
  scope_type: string;
  scope_id: string;
  status: JobStatus;
  scheduled_at?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  attempt_count: number;
  max_attempts: number;
  cancel_requested: boolean;
  waiting_on_id?: string | null;
  error?: string | null;
  result?: Record<string, unknown> | null;
  claimed_by_user_id?: string | null;
  requested_by_user_id?: string | null;
  parent_job_id?: string | null;
}

export type AiTeamQuestionStatus = "pending" | "answered" | "denied" | "timeout";

/** How a resolved question was answered. Absent on legacy rows. */
export type AiTeamAnsweredVia = "text" | "option" | "recommendation";

export interface AiTeamQuestion {
  id: string;
  status: string;
  urgency: string;
  question_text?: string | null;
  /** Structured asks (context → question → recommendation → reasoning).
   * All null on legacy single-blob rows — the card falls back to rendering
   * `question_text` alone. */
  question_context?: string | null;
  recommendation?: string | null;
  reasoning?: string | null;
  answered_via?: AiTeamAnsweredVia | null;
  /** Server-composed full text (question + structure) for single-text
   * clients; null on tool-confirmation rows. */
  composed_text?: string | null;
  options?: string[] | null;
  answer_text?: string | null;
  inbox_email: string;
  requested_by_user_id?: string | null;
  requested_by_slug?: string | null;
  addressed_to_user_id?: string | null;
  addressed_to_slug?: string | null;
  answered_by_user_id?: string | null;
  parent_confirmation_id?: string | null;
  job_id?: string | null;
  created_at?: string | null;
  resolved_at?: string | null;
  escalated_at?: string | null;
}

export interface AiTeamReportEmployee {
  agent_id: string;
  agent_slug?: string | null;
  name?: string | null;
  jobs_completed: number;
  jobs_failed: number;
  jobs_canceled: number;
  jobs_rejected: number;
  avg_duration_s?: number | null;
  questions_asked: number;
  questions_escalated: number;
  tokens: { total_tokens: number; total_cost: number };
}

export interface AiTeamDigest {
  agent_id: string;
  job_id: string;
  date?: string | null;
  digest_md?: string | null;
}

export interface AiTeamTeamReport {
  agent_id: string;
  job_id: string;
  date?: string | null;
  report_md?: string | null;
}

export interface AiTeamReports {
  window_days: number;
  since: string;
  employees: AiTeamReportEmployee[];
  digests: AiTeamDigest[];
  team_reports: AiTeamTeamReport[];
}

export interface AgentCreatePayload {
  agent_slug: string;
  name: string;
  /** agent-worker | agent-cos | agent-mailroom | agent-growth | agent-travel */
  role_name: string;
  owner_user_id?: string | null;
}

export interface AgentPrincipal {
  id: string;
  agent_slug?: string | null;
  name?: string | null;
  email: string;
  owner_user_id?: string | null;
  disabled_at?: string | null;
  created_at?: string | null;
  role_names: string[];
  tokens: AgentToken[];
}

/** The 201 body — carries the plaintext token, which is never shown again. */
export interface AgentCreated extends AgentPrincipal {
  token: string;
}

export interface AgentTokenMinted {
  id: string;
  token_prefix: string;
  /** Plaintext, returned once. */
  token: string;
  revoked_others: number;
}

export interface JobActionResult {
  job: AiTeamJob;
  /**
   * cancel: canceled | cancel_requested | noop.
   * retry: requeued | already_queued — `already_queued` means a live row for
   * the same dedup tuple already carries the work, and `job` is that row.
   */
  action?: string;
  /** retry only: sibling `failed` rows canceled as duplicates of this one. */
  collapsed?: string[];
}

/** One row of the "needs you now" triage list (`GET /ai-team/attention`). */
export interface AiTeamAttentionItem {
  kind: "question" | "review" | "agent" | "job_failure";
  id: string;
  agent_id?: string | null;
  agent_slug?: string | null;
  title: string;
  urgency?: string | null;
  age_s: number;
  job_id?: string | null;
  question_id?: string | null;
  review_id?: string | null;
  /** On kind="review" items: "doc" | "email" | "briefing" (older servers omit it). */
  review_kind?: ReviewKind;
  priority?: number | null;
  topic?: ReviewTopic | null;
  /** On kind="review" items: the review came back from a snooze. */
  returned?: boolean;
  /** Briefing items: UTC instant the meeting starts. */
  meeting_at?: string | null;
  phase?: BriefingPhase | null;
  /** Briefing items: what the owner owes – the debrief (held) or a read. */
  needs?: "debrief" | "read";
}

export interface AiTeamAttention {
  items: AiTeamAttentionItem[];
  counts: {
    blocking: number;
    pending_questions: number;
    pending_reviews: number;
    /** Pending kind='email' review rows — older servers omit it. */
    pending_email_drafts?: number;
    /** Briefings that wait on the owner: held (debrief open), or upcoming
     * within 48 h with an unread version — not in `pending_reviews`. */
    pending_briefings?: number;
    agents_alerting: number;
    recent_failures: number;
  };
  generated_at: string;
}

// ---------- Goals ----------

export type GoalPeriod = "week" | "month" | "quarter";
export type GoalStatus = "active" | "paused" | "achieved" | "retired";
/** computed = server metric · reported = summed progress entries ·
 * unavailable = unknown metric_key (current_value and pace are null). */
export type GoalMeasurement = "computed" | "reported" | "unavailable";

export interface AdminGoalPace {
  elapsed_fraction: number;
  expected_now: number;
  delta: number;
  on_track: boolean;
  pct_of_target: number;
}

/** One goal with its computed pace — `GET /admin/goals` wire shape
 * (`system/services/goals.py :: serialize_goal`). */
export interface AdminGoal {
  id: string;
  title: string;
  description?: string | null;
  metric_key?: string | null;
  unit?: string | null;
  target_value: number;
  period: GoalPeriod;
  status: GoalStatus;
  assigned_agent_id?: string | null;
  assigned_agent_slug?: string | null;
  window: { start: string; end: string; label: string };
  measurement: GoalMeasurement;
  current_value?: number | null;
  pace?: AdminGoalPace | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// ---------- Document reviews ----------

export type ReviewStatus =
  "pending" | "changes_requested" | "approved" | "done" | "rejected" | "withdrawn";

/** A review list's status filter: one status, every status, `open` –
 * pending plus changes requested, the rounds still in progress – or
 * `closed` – done plus withdrawn, a briefing's history. */
export type ReviewListFilter = ReviewStatus | "all" | "open" | "closed";

/** The signature an email draft's mailbox appends (its LTM signature file). */
export interface EmailSignature {
  mailbox: string | null;
  html: string | null;
}

/** The mailboxes "Approve & send" can send from, and the mailbox an envelope
 * without one drafts in (`GET /ai-team/email-drafts/send-mailboxes`). */
export interface DirectSendMailboxes {
  mailboxes: string[];
  default: string | null;
}

/** doc = employee-filed git document · email = outbound email draft
 * submitted via email_submit_draft (envelope in `email_meta`) · briefing =
 * prep for a meeting – the meeting ends it, not an approval. */
export type ReviewKind = "doc" | "email" | "briefing";

/** Where an open briefing stands: before its meeting, or held – the
 * meeting's end has passed and the debrief is open. */
export type BriefingPhase = "upcoming" | "held";

/** Inbox queue of a pending review: main, back from snooze, still snoozed.
 * Derived server-side at read time (services/review_snooze.py). */
export type ReviewQueue = "main" | "returned" | "snoozed";

/** A name in the owner's topic catalog – it groups email drafts and documents. */
export interface ReviewTopic {
  id: string;
  name: string;
}

/** The campaign bundle an email item belongs to (admin list + detail). */
export interface ReviewBundleRef {
  id: string;
  title: string;
  campaign_slug: string | null;
  campaign_name: string | null;
}

/** An email draft takes both fields, a document the topic only. */
export interface ReviewTriagePatch {
  topic_name?: string;
  priority?: number;
}

/** The stored email envelope of a kind='email' review — the exact shape the
 * approval briefing replays into email_create_draft. */
export interface EmailDraftMeta {
  to?: string[] | null;
  cc?: string[] | null;
  bcc?: string[] | null;
  subject?: string | null;
  body_format?: string | null;
  reply_to_message_id?: string | null;
  mailbox_address?: string | null;
  attachments?: string[] | null;
  /** The file name the recipient sees for each attachment, aligned with
   * `attachments` – the server name may carry an upload's " (1)" rename. */
  attachment_names?: string[] | null;
  /** Each attachment's path on the owner's Mac, aligned with `attachments`. */
  attachment_local_paths?: (string | null)[] | null;
  /** Local files for the review only – never attached or sent. */
  review_files?: string[] | null;
  /** Attachments the owner signs first – no approval before (server-enforced). */
  signature_required?: string[] | null;
  /** The body in German or English for the owner – never sent. */
  translation_md?: string | null;
  translation_language?: "de" | "en" | null;
  /** Translation of the message this draft answers. */
  reply_translation_md?: string | null;
}

/** One operator comment anchored to a source-line range of a review doc. */
export interface AdminReviewComment {
  id: string;
  review_id: string;
  /** Doc version the anchor was made against — < the doc's current version
   * means the lines may have drifted (the quoted text is the recovery aid). */
  content_version: number;
  line_start: number;
  line_end: number;
  quoted_text: string;
  body_md: string;
  state: "draft" | "sent";
  /** Server flag: anchored against an earlier version than the doc's current
   * one. Older servers omit it — fall back to comparing content_version. */
  stale?: boolean;
  author_user_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** An employee-filed markdown doc under review (`/ai-team/reviews`). */
export interface AdminReviewDoc {
  id: string;
  agent_id: string;
  agent_slug?: string | null;
  path: string;
  title: string;
  /** "doc" | "email" | "briefing" — older servers omit it (treat as "doc"). */
  kind?: ReviewKind;
  /** Briefings: UTC instants the meeting starts and ends; null otherwise. */
  meeting_at?: string | null;
  meeting_end_at?: string | null;
  /** Briefings: the last version the owner opened; null = none yet. */
  seen_version?: number | null;
  /** Open briefings only (pending / changes requested); null otherwise. */
  phase?: BriefingPhase | null;
  /** Envelope of a kind='email' review; null on docs. */
  email_meta?: EmailDraftMeta | null;
  priority?: number | null;
  topic?: ReviewTopic | null;
  created_at?: string | null;
  filed_on?: string | null;
  content_version: number;
  baseline_commit?: string | null;
  status: ReviewStatus;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  completed_at?: string | null;
  /** UTC instant the row returns from a snooze; null when never snoozed. */
  snoozed_until?: string | null;
  /** Last snooze duration (a SNOOZE_LADDER rung) — the next pick escalates from it. */
  snooze_days?: number | null;
  snooze_count?: number;
  /** Pending rows only; null for history. Older servers omit it (= main). */
  queue?: ReviewQueue | null;
  /** The campaign bundle an email item belongs to (campaign console). */
  bundle_id?: string | null;
  /** That bundle with its campaign – admin list and detail routes only. */
  bundle?: ReviewBundleRef | null;
  /** UTC instant the dates an email offers run out. */
  valid_until?: string | null;
  /** Open item past `valid_until` – its offered dates ran out. */
  expired?: boolean;
  /** UTC instant the owner last acted on this email (a finish or a draft
   * comment) – the inbox's "Recently worked on" group; null = not in it. */
  owner_worked_at?: string | null;
  /** List routes only – the owner's unsent draft comments. */
  draft_comment_count?: number;
  /** Detail route only. */
  content_md?: string;
  /** Detail route only — drafts included (admin surface). */
  comments?: AdminReviewComment[];
  /** Detail route only, emails — the signature the mailbox draft or the send
   * appends; `html` null = none on file for that mailbox. */
  signature?: EmailSignature | null;
  entries?: ReviewEntry[];
  /** Finish response only. */
  feedback_job_id?: string;
  /** Close response only (briefings) — the author's follow-up job. */
  followup_job_id?: string;
  /** Finish response only — sent comments anchored to an earlier version. */
  stale_draft_count?: number;
  /** Set once "Approve & send" had the server send the email from the owner's
   * mailbox (services/outreach/direct.py) – kept on the row, so a sent email
   * reads apart from a drafted one; null otherwise. */
  sent_directly?: {
    mailbox: string;
    message_id: string | null;
    conversation_id?: string | null;
    sent_at: string;
    /** The files that went out with it. */
    attachments?: string[];
  } | null;
  /** Finish response only — the requested send did not happen (refused or
   * failed); the draft flow applies. */
  direct_send_error?: string;
}

export interface ReviewEntry {
  id: string;
  review_id: string;
  content_version: number;
  /** debrief = the owner's notes from closing a briefing. */
  kind: "question" | "answer" | "note" | "changelog" | "debrief";
  body_md: string;
  recorded_question_md: string | null;
  reply_to_id: string | null;
  author_user_id: string | null;
  author_label: string;
  request_id: string;
  created_at: string;
  job_id: string | null;
  delivery_state: "queued" | "working" | "waiting" | "answered" | "failed" | "canceled" | "unanswered" | null;
}

export interface ReviewQuestionPayload {
  request_id: string;
  body_md: string;
  content_version: number;
}

export interface ReviewCommentPayload {
  line_start: number;
  line_end: number;
  quoted_text: string;
  body_md: string;
}

/** One immutable snapshot of a submitted review-doc version. */
export interface AdminReviewVersion {
  review_id: string;
  content_version: number;
  title: string;
  /** Envelope snapshot of a kind='email' review; null on docs. */
  email_meta?: EmailDraftMeta | null;
  baseline_commit?: string | null;
  submitted_at?: string | null;
  /** Version-detail route only. */
  content_md?: string;
}

export type ReviewDiffOp = "ctx" | "add" | "del";

export interface ReviewDiffLine {
  op: ReviewDiffOp;
  text: string;
}

/** One hunk of a structured version diff — 1-based per-side line numbers. */
export interface ReviewDiffHunk {
  from_start: number;
  from_count: number;
  to_start: number;
  to_count: number;
  lines: ReviewDiffLine[];
}

export interface ReviewDiff {
  from_version: number;
  to_version: number;
  hunks: ReviewDiffHunk[];
  added_lines: number;
  removed_lines: number;
  identical: boolean;
}

/** `GET /pending-jobs/{id}` — the job with everything it is connected to. */
export interface AiTeamJobDetail {
  job: AiTeamJob;
  /** The job that delegated this one, when there is one. */
  parent?: AiTeamJob | null;
  /** Work this job delegated onward, oldest first. */
  children: AiTeamJob[];
  /** Every question row pointing at this job, pending and resolved. */
  questions: AiTeamQuestion[];
}

// ---------- Email trust ----------

export type EmailVerdict = "cleared" | "suspicious" | "quarantined";

/** What the feed shows: one verdict, every verdict, or the open queue. */
export type EmailTrustFilter = EmailVerdict | "needs_decision" | "all";

/** Exchange Online's sender verdict (system/services/email_auth.py). */
export type EmailSenderAuth = "internal" | "verified" | "failed" | "unknown";

/** What a release teaches the gate (the `trust` query parameter). */
export type EmailReleaseTrust = "sender" | "domain" | "none";

export interface EmailTrustRow {
  id: string;
  user_id: string;
  message_id: string;
  mailbox_address?: string | null;
  sender?: string | null;
  subject?: string | null;
  received_at?: string | null;
  verdict: EmailVerdict;
  score?: number | null;
  classifier_notes?: string | null;
  sender_auth?: EmailSenderAuth | null;
  decided_by_user_id?: string | null;
  decided_at?: string | null;
  created_at?: string | null;
}

export interface EmailReleaseResult {
  trust: EmailTrustRow;
  trusted_sender?: string | null;
  also_cleared: number;
  sender_trust_note?: string | null;
}

export interface EmailBlacklistResult {
  trust: EmailTrustRow;
  routing_rule_id?: string | null;
  routing_rule_note?: string | null;
  blocked_sender?: string | null;
}

export type SenderTrustStatus = "trusted" | "blocked";

export interface EmailSenderTrustRow {
  id: string;
  user_id: string;
  sender_email: string;
  status: SenderTrustStatus;
  source: string;
  created_by_user_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AdminCampaignCronDefaultsPatch {
  runner_user_id?: string | null;  // "" to clear
  default_notification_target?: AdminNotificationTarget | Record<string, never> | null;  // {} to clear
}

// ---------- Mutation/query payload shapes ----------

export interface UserCreatePayload {
  email: string;
  name?: string;
  role_names?: string[];
  cron_enabled?: boolean;
}

export interface UserPatchPayload {
  name?: string | null;
  slack_user_id?: string | null;
  cron_enabled?: boolean;
  default_slack_channel?: string | null;
}

export interface RoleCreatePayload {
  name: string;
  workspace_slug: string;
  description?: string | null;
  tool_names: string[];
}

export interface RolePatchPayload {
  name?: string;
  description?: string | null;
  tool_names?: string[];
}

export interface ToolPatchPayload {
  /** null → reset to default; true/false → override. */
  requires_confirmation: boolean | null;
}

export interface AuditFilters {
  user?: string;
  tool?: string;
  since?: string;
  limit?: number;
}

export interface DenialFilters {
  since?: string;
  attempted_email?: string;
  limit?: number;
}

export interface ScheduledJobCreatePayload {
  user_id: string;
  workspace_slug: string;
  name: string;
  frequency: string;
  earliest_time_of_day?: string | null;
  latest_time_of_day?: string | null;
  prompt_template: string;
}

export interface ScheduledJobPatchPayload {
  frequency?: string;
  earliest_time_of_day?: string | null;
  latest_time_of_day?: string | null;
  prompt_template?: string;
  enabled?: boolean;
}

// ---------- Helpers ----------

const BASE = "/api/v1/admin";

const KEYS = {
  users: ["admin", "users"] as const,
  user: (id: string) => ["admin", "user", id] as const,
  roles: ["admin", "roles"] as const,
  role: (id: string) => ["admin", "role", id] as const,
  tools: ["admin", "tools"] as const,
  audit: (filters: AuditFilters) => ["admin", "audit", filters] as const,
  denials: (filters: DenialFilters) => ["admin", "denials", filters] as const,
  sessions: ["admin", "sessions"] as const,
  confirmations: (status?: ConfirmationStatus) =>
    ["admin", "confirmations", status ?? "pending"] as const,
  scheduledJobs: (userId?: string) =>
    ["admin", "scheduled-jobs", userId ?? "all"] as const,
  health: ["admin", "health"] as const,
  cronJobs: (filters?: { name?: string; scope_type?: string; scope_id?: string }) =>
    ["admin", "cron-jobs", filters ?? {}] as const,
  cronJob: (id: string) => ["admin", "cron-job", id] as const,
  cronTemplates: ["admin", "cron-templates"] as const,
  scopeTypes: ["admin", "scope-types"] as const,
  scopeChoices: (typeName: string) => ["admin", "scope-choices", typeName] as const,
  scopeGroups: (typeName: string) => ["admin", "scope-groups", typeName] as const,
  scopeGroup: (typeName: string, id: string) =>
    ["admin", "scope-group", typeName, id] as const,
  // The "ai-team" segment is the historical name — the pages now live under
  // /team, but the endpoints stay under /api/v1/admin and the SSE handler
  // invalidates the ["admin","ai-team"] subtree, so the keys keep it.
  aiTeamAgents: ["admin", "ai-team", "agents"] as const,
  aiTeamAgent: (id: string) => ["admin", "ai-team", "agent", id] as const,
  aiTeamAgentJobs: (id: string, status?: JobStatus) =>
    ["admin", "ai-team", "agent-jobs", id, status ?? "all"] as const,
  aiTeamQuestions: (status: string, requestedBy?: string) =>
    ["admin", "ai-team", "questions", status, requestedBy ?? "team"] as const,
  aiTeamJobDetail: (id: string) =>
    ["admin", "ai-team", "job-detail", id] as const,
  aiTeamAttention: ["admin", "ai-team", "attention"] as const,
  aiTeamReviews: (status: string, kind?: string) =>
    ["admin", "ai-team", "reviews", status, kind ?? "all"] as const,
  aiTeamReview: (id: string) => ["admin", "ai-team", "review", id] as const,
  aiTeamReviewVersions: (id: string) =>
    ["admin", "ai-team", "review-versions", id] as const,
  aiTeamReviewVersion: (id: string, v: number) =>
    ["admin", "ai-team", "review-version", id, v] as const,
  aiTeamReviewDiff: (id: string, from: number, to: number) =>
    ["admin", "ai-team", "review-diff", id, from, to] as const,
  aiTeamReports: (days: number) => ["admin", "ai-team", "reports", days] as const,
  aiTeamGoals: (status?: string) =>
    ["admin", "ai-team", "goals", status ?? "active"] as const,
  aiTeamGoal: (id: string) => ["admin", "ai-team", "goal", id] as const,
  emailTrust: (filter: EmailTrustFilter) =>
    ["admin", "email-trust", filter] as const,
  emailSenders: (status?: SenderTrustStatus) =>
    ["admin", "email-senders", status ?? "all"] as const,
};

// ---------- User hooks ----------

function useUsersList() {
  return useQuery({
    queryKey: KEYS.users,
    queryFn: () => api.get<AdminUser[]>(`${BASE}/users`),
  });
}

function useUserGet(
  id: string | null | undefined,
  options?: Partial<UseQueryOptions<AdminUser, Error>>,
) {
  return useQuery({
    queryKey: id ? KEYS.user(id) : ["admin", "user", "_none"],
    queryFn: () => api.get<AdminUser>(`${BASE}/users/${id}`),
    enabled: !!id,
    ...options,
  });
}

function useUserCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserCreatePayload) =>
      api.post<AdminUser>(`${BASE}/users`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
    },
  });
}

function useUserPatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserPatchPayload) =>
      api.patch<AdminUser>(`${BASE}/users/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
      qc.invalidateQueries({ queryKey: KEYS.user(id) });
    },
  });
}

function useUserDisable(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AdminUser>(`${BASE}/users/${id}/disable`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
      qc.invalidateQueries({ queryKey: KEYS.user(id) });
    },
  });
}

function useUserResyncSlack(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<AdminUser>(`${BASE}/users/${id}/resync-slack`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
      qc.invalidateQueries({ queryKey: KEYS.user(id) });
    },
  });
}

function useUserEnable(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AdminUser>(`${BASE}/users/${id}/enable`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
      qc.invalidateQueries({ queryKey: KEYS.user(id) });
    },
  });
}

function useUserDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
    },
  });
}

function useUserOnboardingEmail(id: string) {
  return useMutation({
    mutationFn: () =>
      api.post<OnboardingEmailDraft>(`${BASE}/users/${id}/onboarding-email`),
  });
}

function useUserGrantRole(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) =>
      api.post<{ ok: boolean }>(`${BASE}/users/${userId}/roles`, {
        role_id: roleId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
      qc.invalidateQueries({ queryKey: KEYS.user(userId) });
    },
  });
}

function useUserRevokeRole(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) =>
      api.delete<void>(`${BASE}/users/${userId}/roles/${roleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.users });
      qc.invalidateQueries({ queryKey: KEYS.user(userId) });
    },
  });
}

// ---------- Role hooks ----------

function useRolesList() {
  return useQuery({
    queryKey: KEYS.roles,
    queryFn: () => api.get<AdminRole[]>(`${BASE}/roles`),
  });
}

function useRoleGet(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? KEYS.role(id) : ["admin", "role", "_none"],
    queryFn: () => api.get<AdminRole>(`${BASE}/roles/${id}`),
    enabled: !!id,
  });
}

function useRoleCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RoleCreatePayload) =>
      api.post<AdminRole>(`${BASE}/roles`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.roles });
    },
  });
}

function useRolePatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RolePatchPayload) =>
      api.patch<AdminRole>(`${BASE}/roles/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.roles });
      qc.invalidateQueries({ queryKey: KEYS.role(id) });
      // Roles affect users.role_names display
      qc.invalidateQueries({ queryKey: KEYS.users });
    },
  });
}

function useRoleDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/roles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.roles });
      qc.invalidateQueries({ queryKey: KEYS.users });
    },
  });
}

// ---------- Tool hooks ----------

function useToolsList() {
  return useQuery({
    queryKey: KEYS.tools,
    queryFn: () => api.get<AdminTool[]>(`${BASE}/tools`),
  });
}

function useToolPatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; payload: ToolPatchPayload }) =>
      api.patch<AdminTool>(`${BASE}/tools/${vars.name}`, vars.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.tools });
    },
  });
}

// ---------- Audit / denials / sessions / confirmations hooks ----------

function useAuditQuery(filters: AuditFilters = {}) {
  return useQuery({
    queryKey: KEYS.audit(filters),
    queryFn: () =>
      api.get<AuditEntry[]>(`${BASE}/audit`, {
        user: filters.user,
        tool: filters.tool,
        since: filters.since,
        limit: filters.limit ?? 100,
      }),
  });
}

function useDenialsQuery(filters: DenialFilters = {}) {
  return useQuery({
    queryKey: KEYS.denials(filters),
    queryFn: () =>
      api.get<DenialEntry[]>(`${BASE}/denials`, {
        since: filters.since,
        attempted_email: filters.attempted_email,
        limit: filters.limit ?? 100,
      }),
  });
}

function useSessionsList() {
  return useQuery({
    queryKey: KEYS.sessions,
    queryFn: () => api.get<SessionEntry[]>(`${BASE}/sessions`),
    refetchInterval: 30_000,
  });
}

function useConfirmationsList(status: ConfirmationStatus = "pending", limit = 100) {
  return useQuery({
    queryKey: KEYS.confirmations(status),
    queryFn: () =>
      api.get<ConfirmationEntry[]>(`${BASE}/confirmations`, {
        status,
        limit,
      }),
    refetchInterval: 5_000,
  });
}

// ---------- Scheduled jobs hooks ----------

function useScheduledJobsList(userId?: string) {
  return useQuery({
    queryKey: KEYS.scheduledJobs(userId),
    queryFn: () =>
      api.get<ScheduledJob[]>(`${BASE}/scheduled-jobs`, {
        user_id: userId,
      }),
  });
}

function useScheduledJobCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScheduledJobCreatePayload) =>
      api.post<ScheduledJob>(`${BASE}/scheduled-jobs`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "scheduled-jobs"] });
    },
  });
}

function useScheduledJobPatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScheduledJobPatchPayload) =>
      api.patch<ScheduledJob>(`${BASE}/scheduled-jobs/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "scheduled-jobs"] });
    },
  });
}

function useScheduledJobDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/scheduled-jobs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "scheduled-jobs"] });
    },
  });
}

// ---------- Cron job hooks ----------

function useCronJobs(filters?: { name?: string; scope_type?: string; scope_id?: string }) {
  const params = new URLSearchParams();
  if (filters?.name) params.set("name", filters.name);
  if (filters?.scope_type) params.set("scope_type", filters.scope_type);
  if (filters?.scope_id) params.set("scope_id", filters.scope_id);
  const qs = params.toString();
  return useQuery({
    queryKey: KEYS.cronJobs(filters),
    queryFn: () =>
      api.get<AdminCronJob[]>(`${BASE}/cron-jobs${qs ? `?${qs}` : ""}`),
  });
}

function useCronJob(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? KEYS.cronJob(id) : ["admin", "cron-job", "_none"],
    queryFn: () => api.get<AdminCronJob>(`${BASE}/cron-jobs/${id}`),
    enabled: !!id,
  });
}

function useCronJobCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminCronJobCreate) =>
      api.post<AdminCronJob>(`${BASE}/cron-jobs`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cron-jobs"] });
    },
  });
}

function useCronJobPatch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminCronJobPatch) =>
      api.patch<AdminCronJob>(`${BASE}/cron-jobs/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cron-jobs"] });
      qc.invalidateQueries({ queryKey: KEYS.cronJob(id) });
    },
  });
}

function useCronJobDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/cron-jobs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cron-jobs"] });
    },
  });
}

function useCronJobTemplates() {
  return useQuery({
    queryKey: KEYS.cronTemplates,
    queryFn: () =>
      api.get<AdminCronJobTemplate[]>(`${BASE}/cron-jobs/templates`),
  });
}

function useCronValidateSchedule() {
  return useMutation({
    mutationFn: (schedule: string) =>
      api.post<AdminCronValidateResult>(
        `${BASE}/cron-jobs/validate-schedule`,
        { schedule },
      ),
  });
}

function useCronValidateTokens() {
  return useMutation({
    mutationFn: (payload: { template: string; scope_type: string }) =>
      api.post<AdminCronTokensValidateResult>(
        `${BASE}/cron-jobs/validate-tokens`,
        payload,
      ),
  });
}

function useCronSyncToScheduler() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<AdminCronSyncResult>(`${BASE}/cron-jobs/sync`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cron-jobs"] });
    },
  });
}

function useScopeTypes() {
  return useQuery({
    queryKey: KEYS.scopeTypes,
    queryFn: () => api.get<AdminScopeType[]>(`${BASE}/scopes/types`),
  });
}

function useScopeChoices(typeName: string | null | undefined) {
  return useQuery({
    queryKey: typeName ? KEYS.scopeChoices(typeName) : ["admin", "scope-choices", "_none"],
    queryFn: () => api.get<AdminScopeChoice[]>(`${BASE}/scopes/${typeName}`),
    enabled: !!typeName,
  });
}

function useScopeGroups(typeName: AdminScopeGroupType) {
  return useQuery({
    queryKey: KEYS.scopeGroups(typeName),
    queryFn: () =>
      api.get<AdminScopeGroup[]>(`${BASE}/scopes/${typeName}/groups`),
  });
}

function useScopeGroup(
  typeName: AdminScopeGroupType,
  id: string | null | undefined,
) {
  return useQuery({
    queryKey: id
      ? KEYS.scopeGroup(typeName, id)
      : ["admin", "scope-group", "_none"],
    queryFn: () =>
      api.get<AdminScopeGroupDetail>(`${BASE}/scopes/${typeName}/groups/${id}`),
    enabled: !!id,
  });
}

function useScopeGroupCreate(typeName: AdminScopeGroupType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminScopeGroupCreate) =>
      api.post<AdminScopeGroupDetail>(
        `${BASE}/scopes/${typeName}/groups`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scopeGroups(typeName) });
    },
  });
}

function useScopeGroupPatch(typeName: AdminScopeGroupType, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminScopeGroupPatch) =>
      api.patch<AdminScopeGroupDetail>(
        `${BASE}/scopes/${typeName}/groups/${id}`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scopeGroups(typeName) });
      qc.invalidateQueries({ queryKey: KEYS.scopeGroup(typeName, id) });
    },
  });
}

function useScopeGroupDelete(typeName: AdminScopeGroupType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/scopes/${typeName}/groups/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scopeGroups(typeName) });
    },
  });
}

function useScopeGroupMemberPut(
  typeName: AdminScopeGroupType,
  groupId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: string; role: string }) =>
      api.put<AdminScopeGroupMember>(
        `${BASE}/scopes/${typeName}/groups/${groupId}/members/${vars.userId}`,
        { role: vars.role },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scopeGroups(typeName) });
      qc.invalidateQueries({ queryKey: KEYS.scopeGroup(typeName, groupId) });
    },
  });
}

function useScopeGroupMemberRemove(
  typeName: AdminScopeGroupType,
  groupId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<void>(
        `${BASE}/scopes/${typeName}/groups/${groupId}/members/${userId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.scopeGroups(typeName) });
      qc.invalidateQueries({ queryKey: KEYS.scopeGroup(typeName, groupId) });
    },
  });
}

// ---------- AI team hooks ----------
//
// Every write in this section changes something the employee cards show
// (queue counts, open questions, suspension), so the mutations invalidate the
// whole `["admin", "ai-team"]` subtree rather than trying to predict which
// card moved.

function invalidateAiTeam(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["admin", "ai-team"] });
}

function useAiTeamAgents() {
  return useQuery({
    queryKey: KEYS.aiTeamAgents,
    queryFn: () => api.get<AiTeamAgent[]>(`${BASE}/ai-team/agents`),
    refetchInterval: 10_000,
  });
}

function useAiTeamAgent(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? KEYS.aiTeamAgent(id) : ["admin", "ai-team", "agent", "_none"],
    queryFn: () => api.get<AiTeamAgentDetail>(`${BASE}/ai-team/agents/${id}`),
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

function useAiTeamAgentJobs(
  id: string | null | undefined,
  status?: JobStatus,
  limit = 100,
) {
  return useQuery({
    queryKey: id
      ? KEYS.aiTeamAgentJobs(id, status)
      : ["admin", "ai-team", "agent-jobs", "_none"],
    queryFn: () =>
      api.get<AiTeamJob[]>(`${BASE}/ai-team/agents/${id}/jobs`, {
        status,
        limit,
      }),
    enabled: !!id,
    refetchInterval: 30_000,
  });
}

/**
 * `"all"` asks for every status — the endpoint defaults to pending.
 * `requestedBy` filters server-side to one asker, so a per-employee view
 * sees that employee's full retained log instead of a client-side slice of
 * the team-wide page. Rows arrive in the server's triage order (pending
 * first, blocking pinned, oldest on top; then history newest-first).
 */
function useAiTeamQuestions(
  status: AiTeamQuestionStatus | "all",
  opts?: { requestedBy?: string; limit?: number },
) {
  const requestedBy = opts?.requestedBy;
  const limit = opts?.limit ?? 200;
  return useQuery({
    queryKey: KEYS.aiTeamQuestions(status, requestedBy),
    queryFn: () => {
      // An absent `status` means "pending" server-side, so "all" has to be an
      // explicit empty value — which the params helper would strip.
      const qs = new URLSearchParams({
        status: status === "all" ? "" : status,
        limit: String(limit),
      });
      if (requestedBy) qs.set("requested_by", requestedBy);
      return api.get<AiTeamQuestion[]>(`${BASE}/ai-team/questions?${qs}`);
    },
    refetchInterval: 30_000,
  });
}

/** One question by id, as a one-row list – the Questions page renders it
 * like the log (`?id=`, the target of an ID link). */
function useAiTeamQuestion(id: string | null) {
  return useQuery({
    queryKey: ["admin", "ai-team", "question", id ?? "_none"],
    queryFn: () => api.get<AiTeamQuestion>(`${BASE}/ai-team/questions/${id}`),
    select: (q) => [q],
    enabled: !!id,
    refetchInterval: 30_000,
  });
}

/** Answer body: free text, or the one-tap `accept_recommendation` flag
 * (the server resolves the answer to the row's recommendation). */
export interface AiTeamAnswerBody {
  answer?: string;
  accept_recommendation?: boolean;
}

function useAiTeamAnswerQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string } & AiTeamAnswerBody) =>
      api.post<{ status: string }>(
        `${BASE}/ai-team/questions/${vars.id}/answer`,
        vars.accept_recommendation
          ? { accept_recommendation: true }
          : { answer: vars.answer },
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useAiTeamDenyQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      api.post<{ status: string }>(
        `${BASE}/ai-team/questions/${vars.id}/deny`,
        { reason: vars.reason ?? null },
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/** The "needs you now" list — server-ordered, counts for the chip. */
function useAiTeamAttention() {
  return useQuery({
    queryKey: KEYS.aiTeamAttention,
    queryFn: () => api.get<AiTeamAttention>(`${BASE}/ai-team/attention`),
    refetchInterval: 30_000,
  });
}

/** Drill-down read: the job plus its delegation lineage and questions. */
function useAiTeamJobDetail(jobId: string | null | undefined) {
  return useQuery({
    queryKey: jobId
      ? KEYS.aiTeamJobDetail(jobId)
      : ["admin", "ai-team", "job-detail", "_none"],
    queryFn: () => api.get<AiTeamJobDetail>(`${BASE}/pending-jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: 30_000,
  });
}

// ---------- Document review hooks ----------

/** The server's status filter for a list filter – `open` and `closed` are comma lists. */
function reviewStatusParam(status: ReviewListFilter): string | undefined {
  if (status === "all") return undefined;
  if (status === "open") return "pending,changes_requested";
  return status === "closed" ? "done,withdrawn" : status;
}

/**
 * `"all"` lists every status, `"open"` pending plus changes requested,
 * `"closed"` done plus withdrawn; anything else filters server-side. Rows
 * arrive in the server's triage order (open rounds first — priority
 * ascending, oldest filing on top, the todo/folder's own order; a
 * changes-requested row keeps its place — then history newest-first).
 * Open briefings come by meeting time instead, held ones first.
 */
function useAiTeamReviews(
  status: ReviewListFilter,
  kind?: ReviewKind,
  limit = 100,
) {
  return useQuery({
    queryKey: KEYS.aiTeamReviews(status, kind),
    queryFn: async ({ signal }) => {
      const rows: AdminReviewDoc[] = [];
      // Email group counts and navigation use the complete inbox, never a
      // silently truncated first page. Keep summaries only (no email bodies).
      let offset = 0;
      for (;;) {
        const page = await api.get<AdminReviewDoc[]>(`${BASE}/ai-team/reviews`, {
          status: reviewStatusParam(status), kind, limit, offset,
        }, signal);
        rows.push(...page);
        // Pending and open lists are split into queues (briefings into
        // meeting days) client-side, so they must be complete too — a
        // snoozed tail must not hide behind the page limit.
        if ((kind !== "email" && status !== "pending" && status !== "open") || page.length < limit) break;
        offset += page.length;
      }
      return [...new Map(rows.map((row) => [row.id, row])).values()];
    },
    refetchInterval: 30_000,
  });
}

function useAiTeamReview(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? KEYS.aiTeamReview(id) : ["admin", "ai-team", "review", "_none"],
    queryFn: () => api.get<AdminReviewDoc>(`${BASE}/ai-team/reviews/${id}`),
    enabled: !!id,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });
}

/** "Recently worked on": the reviews of one kind the owner acted on, newest
 * action first (any status, the last 14 days). Inside the reviews key
 * family, so every finish and comment repaints it. */
function useRecentReviews(kind: ReviewKind) {
  return useQuery({
    queryKey: ["admin", "ai-team", "reviews", "recent", kind],
    queryFn: () => api.get<AdminReviewDoc[]>(`${BASE}/ai-team/reviews/recent?kind=${kind}`),
    refetchInterval: 30_000,
  });
}

/** Take a review out of "Recently worked on" (`workedAt: null`) or put it
 * back at its earlier time – the X and its Undo. */
function useReviewRecentSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; workedAt: string | null }) =>
      api.put<AdminReviewDoc>(`${BASE}/ai-team/reviews/${vars.reviewId}/recent`, {
        worked_at: vars.workedAt,
      }),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/** The mailboxes "Approve & send" can send from (lower-cased) and the
 * default mailbox an envelope without one drafts in. */
function useDirectSendMailboxes() {
  return useQuery({
    queryKey: ["admin", "ai-team", "email-draft-send-mailboxes"],
    queryFn: () => api.get<DirectSendMailboxes>(`${BASE}/ai-team/email-drafts/send-mailboxes`),
    staleTime: 60_000,
    retry: false,
  });
}

function useReviewTopics() {
  return useQuery({
    queryKey: ["admin", "ai-team", "email-draft-topics"],
    queryFn: () => api.get<ReviewTopic[]>(`${BASE}/ai-team/email-draft-topics`),
    refetchInterval: 30_000,
  });
}

function useReviewTriageUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; patch: ReviewTriagePatch }) =>
      api.patch<AdminReviewDoc>(`${BASE}/ai-team/reviews/${vars.reviewId}/triage`, vars.patch),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/**
 * Version list for the switcher — no bodies. No refetchInterval: the doc's
 * own 5 s poll plus the whole-subtree invalidation cover resubmits.
 * A 404 (backend without the versions surface) degrades the UI to
 * current-version-only via the query's error state.
 */
function useAiTeamReviewVersions(id: string | null | undefined) {
  return useQuery({
    queryKey: id
      ? KEYS.aiTeamReviewVersions(id)
      : ["admin", "ai-team", "review-versions", "_none"],
    queryFn: () =>
      api.get<AdminReviewVersion[]>(`${BASE}/ai-team/reviews/${id}/versions`),
    enabled: !!id,
  });
}

/** One snapshot with its body — immutable, so it never goes stale. */
function useAiTeamReviewVersion(
  id: string | null | undefined,
  version: number | null,
) {
  return useQuery({
    queryKey:
      id && version != null
        ? KEYS.aiTeamReviewVersion(id, version)
        : ["admin", "ai-team", "review-version", "_none", 0],
    queryFn: () =>
      api.get<AdminReviewVersion>(
        `${BASE}/ai-team/reviews/${id}/versions/${version}`,
      ),
    enabled: !!id && version != null,
    staleTime: Infinity,
  });
}

/**
 * Structured diff between two snapshots. Immutable per (id, from, to) —
 * `staleTime: Infinity`; the version-keyed queryKey self-invalidates on a
 * resubmit because the caller asks for a new `to`.
 */
function useAiTeamReviewDiff(
  id: string | null | undefined,
  from: number | null,
  to: number,
) {
  return useQuery({
    queryKey:
      id && from != null
        ? KEYS.aiTeamReviewDiff(id, from, to)
        : ["admin", "ai-team", "review-diff", "_none", 0, 0],
    queryFn: () =>
      api.get<ReviewDiff>(`${BASE}/ai-team/reviews/${id}/diff`, {
        from_version: from,
        to_version: to,
      }),
    enabled: !!id && from != null && from >= 1 && from < to,
    staleTime: Infinity,
  });
}

function useReviewCommentAdd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; payload: ReviewCommentPayload }) =>
      api.post<AdminReviewComment>(
        `${BASE}/ai-team/reviews/${vars.reviewId}/comments`,
        vars.payload,
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useReviewQuestionAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; payload: ReviewQuestionPayload }) =>
      api.post<ReviewEntry>(`${BASE}/ai-team/reviews/${vars.reviewId}/questions`, vars.payload),
    onSuccess: (_entry, vars) => {
      void qc.invalidateQueries({ queryKey: KEYS.aiTeamReview(vars.reviewId) });
    },
  });
}

function useReviewCommentUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; commentId: string; bodyMd: string }) =>
      api.patch<AdminReviewComment>(
        `${BASE}/ai-team/reviews/${vars.reviewId}/comments/${vars.commentId}`,
        { body_md: vars.bodyMd },
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useReviewCommentDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; commentId: string }) =>
      api.delete<void>(
        `${BASE}/ai-team/reviews/${vars.reviewId}/comments/${vars.commentId}`,
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useReviewFinish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      reviewId: string;
      decision?: "request_changes" | "approve" | "reject";
      feedback_md?: string;
      /** Approved email only: "send" has the server send it (no comments), "draft"
       * (default) the seat draft it – with the comments worked in. */
      delivery?: "draft" | "send";
    }) =>
      api.post<AdminReviewDoc>(`${BASE}/ai-team/reviews/${vars.reviewId}/finish`, {
        decision: vars.decision ?? null,
        feedback_md: vars.feedback_md ?? "",
        delivery: vars.delivery ?? null,
      }),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/** Defer a pending review for `days` (a SNOOZE_LADDER rung). */
function useReviewSnooze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; days: number }) =>
      api.post<AdminReviewDoc>(`${BASE}/ai-team/reviews/${vars.reviewId}/snooze`, {
        days: vars.days,
      }),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/** Bring a snoozed review back now — it lands in "Back from snooze". */
function useReviewWake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string }) =>
      api.post<AdminReviewDoc>(`${BASE}/ai-team/reviews/${vars.reviewId}/wake`, {}),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/** The owner opened this version of a briefing (monotonic server-side) –
 * its unread dot and its "read" item in Needs you go away. */
function useReviewSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; version: number }) =>
      api.post<AdminReviewDoc>(`${BASE}/ai-team/reviews/${vars.reviewId}/seen`, {
        version: vars.version,
      }),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

/** Close an open briefing, with the owner's debrief notes or without – the
 * notes become a `debrief` entry and the author's follow-up job. */
function useReviewClose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { reviewId: string; notesMd?: string }) =>
      api.post<AdminReviewDoc>(
        `${BASE}/ai-team/reviews/${vars.reviewId}/close`,
        vars.notesMd?.trim() ? { notes_md: vars.notesMd } : {},
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

// ---------- Goal hooks ----------
//
// Read + status-flip only: goal authoring stays on the confirmation-gated
// MCP tools so validation lives once (WebUI is read-mostly by convention).

/** Default = active goals; pass "all" (or a status) to widen. `retry: false`
 * because the endpoint may not be deployed yet — the scoreboard renders
 * nothing rather than hammering a 404. */
function useAiTeamGoals(status?: GoalStatus | "all") {
  return useQuery({
    queryKey: KEYS.aiTeamGoals(status),
    queryFn: () => api.get<AdminGoal[]>(`${BASE}/goals`, { status }),
    retry: false,
    refetchInterval: 30_000,
  });
}

function useAiTeamGoal(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? KEYS.aiTeamGoal(id) : ["admin", "ai-team", "goal", "_none"],
    queryFn: () => api.get<AdminGoal>(`${BASE}/goals/${id}`),
    enabled: !!id,
    retry: false,
  });
}

function useGoalSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: GoalStatus }) =>
      api.post<AdminGoal>(`${BASE}/goals/${vars.id}/status`, {
        status: vars.status,
      }),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useAiTeamReports(days = 7) {
  return useQuery({
    queryKey: KEYS.aiTeamReports(days),
    queryFn: () => api.get<AiTeamReports>(`${BASE}/ai-team/reports`, { days }),
  });
}

function useAgentCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentCreatePayload) =>
      api.post<AgentCreated>(`${BASE}/agents`, payload),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useAgentSuspend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<AgentPrincipal>(`${BASE}/agents/${id}/disable`),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useAgentResume() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<AgentPrincipal>(`${BASE}/agents/${id}/enable`),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useAgentTokenRotate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { agentId: string; label?: string | null }) =>
      api.post<AgentTokenMinted>(`${BASE}/agents/${vars.agentId}/tokens`, {
        label: vars.label ?? null,
        revoke_others: true,
      }),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useAgentTokenRevoke() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { agentId: string; tokenId: string }) =>
      api.delete<AgentToken>(
        `${BASE}/agents/${vars.agentId}/tokens/${vars.tokenId}`,
      ),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useJobCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<JobActionResult>(`${BASE}/pending-jobs/${id}/cancel`),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

function useJobRetry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<JobActionResult>(`${BASE}/pending-jobs/${id}/retry`),
    onSuccess: () => invalidateAiTeam(qc),
  });
}

// ---------- Email trust hooks ----------

function useEmailTrust(filter: EmailTrustFilter, limit = 200) {
  return useQuery({
    queryKey: KEYS.emailTrust(filter),
    queryFn: () =>
      api.get<EmailTrustRow[]>(`${BASE}/email-trust`, {
        verdict:
          filter === "all" || filter === "needs_decision" ? undefined : filter,
        needs_decision: filter === "needs_decision" ? true : undefined,
        limit,
      }),
    refetchInterval: 30_000,
  });
}

function useEmailRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, trust }: { id: string; trust: EmailReleaseTrust }) =>
      api.post<EmailReleaseResult>(
        `${BASE}/email-trust/${id}/release?trust=${trust}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "email-trust"] });
      qc.invalidateQueries({ queryKey: ["admin", "email-senders"] });
    },
  });
}

function useEmailConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<EmailTrustRow>(`${BASE}/email-trust/${id}/confirm`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "email-trust"] });
    },
  });
}

function useEmailBlacklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<EmailBlacklistResult>(`${BASE}/email-trust/${id}/blacklist`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "email-trust"] });
      qc.invalidateQueries({ queryKey: ["admin", "email-senders"] });
    },
  });
}

function useEmailSenders(status?: SenderTrustStatus, limit = 200) {
  return useQuery({
    queryKey: KEYS.emailSenders(status),
    queryFn: () =>
      api.get<EmailSenderTrustRow[]>(`${BASE}/email-trust/senders`, {
        status,
        limit,
      }),
    refetchInterval: 30_000,
  });
}

function useEmailSenderRevoke() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}/email-trust/senders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "email-senders"] });
    },
  });
}

function useCampaignCronDefaultsPatch(slug: string) {
  return useMutation({
    mutationFn: (payload: AdminCampaignCronDefaultsPatch) =>
      api.patch(`${BASE}/campaigns/${slug}/cron-defaults`, payload),
  });
}

// ---------- Health hook ----------

function useHealth() {
  return useQuery({
    queryKey: KEYS.health,
    queryFn: () => api.get<AdminHealth>(`${BASE}/health`),
    refetchInterval: 30_000,
  });
}

// ---------- Public surface ----------

export const adminApi = {
  users: {
    list: useUsersList,
    get: useUserGet,
    create: useUserCreate,
    patch: useUserPatch,
    disable: useUserDisable,
    enable: useUserEnable,
    delete: useUserDelete,
    resyncSlack: useUserResyncSlack,
    onboardingEmail: useUserOnboardingEmail,
    grantRole: useUserGrantRole,
    revokeRole: useUserRevokeRole,
  },
  roles: {
    list: useRolesList,
    get: useRoleGet,
    create: useRoleCreate,
    patch: useRolePatch,
    delete: useRoleDelete,
  },
  tools: {
    list: useToolsList,
    patch: useToolPatch,
  },
  audit: { query: useAuditQuery },
  denials: { query: useDenialsQuery },
  sessions: { list: useSessionsList },
  confirmations: { list: useConfirmationsList },
  scheduledJobs: {
    list: useScheduledJobsList,
    create: useScheduledJobCreate,
    patch: useScheduledJobPatch,
    delete: useScheduledJobDelete,
  },
  cronJobs: {
    list: useCronJobs,
    get: useCronJob,
    create: useCronJobCreate,
    patch: useCronJobPatch,
    delete: useCronJobDelete,
    templates: useCronJobTemplates,
    validateSchedule: useCronValidateSchedule,
    validateTokens: useCronValidateTokens,
    sync: useCronSyncToScheduler,
  },
  scopes: {
    types: useScopeTypes,
    choices: useScopeChoices,
    groups: useScopeGroups,
    group: useScopeGroup,
    createGroup: useScopeGroupCreate,
    patchGroup: useScopeGroupPatch,
    deleteGroup: useScopeGroupDelete,
    putMember: useScopeGroupMemberPut,
    removeMember: useScopeGroupMemberRemove,
  },
  aiTeam: {
    agents: useAiTeamAgents,
    agent: useAiTeamAgent,
    agentJobs: useAiTeamAgentJobs,
    questions: useAiTeamQuestions,
    question: useAiTeamQuestion,
    answerQuestion: useAiTeamAnswerQuestion,
    denyQuestion: useAiTeamDenyQuestion,
    jobDetail: useAiTeamJobDetail,
    attention: useAiTeamAttention,
    reviews: useAiTeamReviews,
    recentReviews: useRecentReviews,
    setReviewRecent: useReviewRecentSet,
    directSendMailboxes: useDirectSendMailboxes,
    reviewTopics: useReviewTopics,
    updateReviewTriage: useReviewTriageUpdate,
    review: useAiTeamReview,
    reviewVersions: useAiTeamReviewVersions,
    reviewVersion: useAiTeamReviewVersion,
    reviewDiff: useAiTeamReviewDiff,
    addReviewComment: useReviewCommentAdd,
    askReviewQuestion: useReviewQuestionAsk,
    updateReviewComment: useReviewCommentUpdate,
    deleteReviewComment: useReviewCommentDelete,
    finishReview: useReviewFinish,
    snoozeReview: useReviewSnooze,
    wakeReview: useReviewWake,
    markReviewSeen: useReviewSeen,
    closeBriefing: useReviewClose,
    goals: useAiTeamGoals,
    goal: useAiTeamGoal,
    setGoalStatus: useGoalSetStatus,
    reports: useAiTeamReports,
    createAgent: useAgentCreate,
    suspendAgent: useAgentSuspend,
    resumeAgent: useAgentResume,
    rotateToken: useAgentTokenRotate,
    revokeToken: useAgentTokenRevoke,
    cancelJob: useJobCancel,
    retryJob: useJobRetry,
  },
  emailTrust: {
    list: useEmailTrust,
    release: useEmailRelease,
    confirm: useEmailConfirm,
    blacklist: useEmailBlacklist,
    senders: useEmailSenders,
    revokeSender: useEmailSenderRevoke,
  },
  campaignCronDefaults: { patch: useCampaignCronDefaultsPatch },
  health: { get: useHealth },
};

export const ADMIN_QUERY_KEYS = KEYS;
